import apiClient from '../client';

// NOTE: the shared apiClient has a response interceptor that returns
// response.data, so calls resolve to the response BODY. Axios's second
// generic (R) types that unwrapped value — hence get<any, BodyShape>.
// The workflow endpoints return { success, instances | instance }.

export type WorkflowInstanceStatus = 'active' | 'completed' | 'failed' | 'cancelled';

export interface WorkflowInstanceStep {
  stepId: string;
  title: string;
  type: 'system_action' | 'human_task' | 'timer' | 'conditional';
  status: 'pending' | 'active' | 'completed' | 'skipped' | 'escalated' | 'failed';
  description?: string;
  proofType?: 'photo' | 'check';
  assignedRole?: string;
  assignedAccountIds?: string[];
  completedBy?: string;
  completedAt?: string;
  startedAt?: string;
  dueAt?: string;
  escalatedAt?: string;
  proofPhotos?: string[];
  notes?: string;
  reason?: string;
  /** Answers the step offers; picking one is completing it. */
  choices?: { label: string; contextPatch: Record<string, unknown> }[];
  /** A small form instead of choices; its answer arrives as contextPatch. */
  form?: 'driver_schedule';
}

export interface WorkflowInstance {
  _id: string;
  templateId: string;
  templateName: string;
  companyId: string;
  serviceAreaId?: number;
  label: string;
  status: WorkflowInstanceStatus;
  currentStepId?: string | null;
  startedAt: string;
  completedAt?: string;
  steps: WorkflowInstanceStep[];
  triggerType: 'manual' | 'time' | 'event';
  updatedAt?: string;
  /** What the run is about, as the event that started it said: a candidateId and name, an obligation. */
  context?: Record<string, any>;
  /**
   * 'operation': something that goes on for days (a new driver's training),
   * holding the runs that serve it. No steps of its own: nobody's task.
   */
  kind?: 'run' | 'operation';
  /** The operation this run is one day of. */
  parentId?: string | null;
  /** An operation's runs, oldest first (detail only). */
  children?: WorkflowInstance[];
}

export class WorkflowService {
  private static readonly BASE_PATH = '/api/workflows';

  // status: a single status, or 'finished' = completed/cancelled/failed.
  static async getInstances(status?: WorkflowInstanceStatus | 'finished'): Promise<WorkflowInstance[]> {
    // Operations come too: the owner's list shows them as ongoing rows.
    const url = status
      ? `${this.BASE_PATH}/instances?status=${status}&include=operations`
      : `${this.BASE_PATH}/instances?include=operations`;
    const body = await apiClient.get<any, { success: boolean; instances: WorkflowInstance[] }>(url);
    return body?.instances ?? [];
  }

  static async getInstance(instanceId: string): Promise<WorkflowInstance> {
    const body = await apiClient.get<any, { success: boolean; instance: WorkflowInstance }>(
      `${this.BASE_PATH}/instances/${instanceId}`
    );
    return body.instance;
  }

  // Signed URL to view a stored proof photo.
  static async getPhotoUrl(path: string): Promise<string | null> {
    try {
      const body = await apiClient.get<any, { success: boolean; url: string }>(
        `${this.BASE_PATH}/photo/${path}`
      );
      return body?.url ?? null;
    } catch {
      return null;
    }
  }

  // Stop a running workflow (soft cancel). SA/CA/AO only.
  /** Complete the step that is waiting: an answer, a note, a form. */
  static async completeStep(instanceId: string, stepId: string, body: { notes?: string; contextPatch?: Record<string, unknown> }): Promise<WorkflowInstance> {
    const res = await apiClient.post<any, { success: boolean; instance: WorkflowInstance }>(
      `${this.BASE_PATH}/instances/${instanceId}/steps/${stepId}/complete`, body,
    );
    return res.instance;
  }

  static async cancelInstance(instanceId: string, reason?: string): Promise<WorkflowInstance> {
    const body = await apiClient.post<any, { success: boolean; instance: WorkflowInstance }>(
      `${this.BASE_PATH}/instances/${instanceId}/cancel`,
      reason ? { reason } : {}
    );
    return body.instance;
  }
}

export default WorkflowService;
